import { nanoid } from 'nanoid';

import { type QuizGenerateItem } from '@/api/ai/type';
import { prepareTiptapHTML } from '@/shared/features/rich-text';
import { extractBareText } from '@/shared/utils/extractBareText';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { getBlankQuizItem } from '../../../form/schema/getBlankQuizItem';

/**
 * AI 응답 텍스트를 plain text 필드용으로 정규화한다.
 *
 * AI 서버는 `<u>`, `<strong>` 같은 인라인 마크업을 포함한 문자열을 반환할 수
 * 있다. tiptap 에디터 경로는 plain text 필드를 `extractBareText`로 정제하지만,
 * 이 어댑터는 에디터를 우회해 폼 상태에 직접 쓰므로 동일한 정제를 거쳐야
 * plain text 필드에 마크업이 새어 들어가지 않는다.
 */
const toPlainText = (content: string): string =>
  extractBareText(prepareTiptapHTML(content));

/**
 * AI 서버로부터 생성된 데이터를 Build Schema에 맞도록 변환하는 helper function
 *
 * @param quizzes - AI 응답 quiz items
 * @param generationId - quiz item에 stamp할 generation_id (다운스트림 funnel attribution용)
 */
export const adaptAiGeneratedQuizzesToBuildForm = (
  quizzes: QuizGenerateItem[],
  generationId?: string,
): BuildQuizListItem[] => {
  const origin = generationId
    ? ({ type: 'ai' as const, generationId } as const)
    : undefined;

  return quizzes.map(quiz => {
    const blankQuiz = getBlankQuizItem();
    switch (quiz.type) {
      case 'multiple':
        return {
          ...blankQuiz,
          ...quiz,
          type: 'multiple' as const,
          // plain text 필드는 마크업을 제거해 저장한다 (tiptap 에디터 경로와 동일).
          question: toPlainText(quiz.question),
          // Rendering될 때 questionRichText -> question으로 fallback 처리를 해놓기는 했지만
          // fallback 처리가 누락되는 경우를 대비해 questionRichText에도 같은 데이터를 저장합니다.
          questionRichText: prepareTiptapHTML(quiz.question),
          multipleData: adaptAiMultipleData(quiz),
          origin,
        };
      case 'ox':
        return {
          ...blankQuiz,
          ...quiz,
          type: 'ox' as const,
          question: toPlainText(quiz.question),
          questionRichText: prepareTiptapHTML(quiz.question),
          oxData: adaptAiOXData(quiz),
          origin,
        };
      case 'subjective':
        return {
          ...blankQuiz,
          ...quiz,
          type: 'subjective' as const,
          question: toPlainText(quiz.question),
          questionRichText: prepareTiptapHTML(quiz.question),
          subjectiveData: adaptAiSubjectiveData(quiz),
          origin,
        };
      case 'essay':
        // AI 생성은 essay 타입을 반환하지 않음. 안전망으로 명시적 throw.
        throw new Error('Essay type is not supported by AI quiz generation');
      default:
        throw new Error('Invalid quiz type');
    }
  });
};

const adaptAiMultipleData = (quiz: QuizGenerateItem) => {
  const choiceList = quiz.options.map((option, index) => ({
    text: toPlainText(option),
    richText: prepareTiptapHTML(option),
    key: nanoid(8),
    isAnswer: quiz.answer.includes(String(index)),
  }));

  return {
    choiceList,
  };
};

const adaptAiSubjectiveData = (quiz: QuizGenerateItem) => {
  return {
    answerList: quiz.answer.map(answer => ({
      text: answer,
      key: nanoid(8),
    })),
  };
};

const adaptAiOXData = (quiz: QuizGenerateItem) => {
  return {
    answer: quiz.answer[0] as 'o' | 'x',
  };
};
